import { Order } from "../../../core/models/entities/Order";
import { formatDate } from "../../../utils/functions/DateUtil";

// Requested delivery date (flag REACT_APP_REQUESTED_DELIVERY_ENABLED).
//
// Order.latestDelivery has always been NAV's shipment date: the API sets it
// from GetShipmentDate when the order is placed and its status jobs keep
// overwriting it with NAV's Shipment Date afterwards - so the date the
// customer actually picked at checkout (sent as latestDeliveryDate) was lost
// from every screen. The paired API PR persists that pick and exposes it as
// OrderDto.requestedDelivery. These helpers are display-only: "Expected"
// stays latestDelivery everywhere, "Requested" is added only when the two
// differ, and with the flag off every helper returns exactly today's value.

type DeliveryDates = Pick<Order, "latestDelivery" | "requestedDelivery">;

function validDate(date?: Date): date is Date {
  return date instanceof Date && !isNaN(date.getTime()) && date.getTime() > 0;
}

// The requested date - only when it differs from the expected date AS
// DISPLAYED (same formatDate output = same calendar day = nothing to add).
export function requestedDeliveryToShow(
  order: DeliveryDates,
  enabled: boolean,
): Date | undefined {
  if (!enabled) return undefined;
  const requested = order.requestedDelivery;
  if (!validDate(requested)) return undefined;
  const expected = order.latestDelivery;
  if (validDate(expected) && formatDate(expected) === formatDate(requested)) {
    return undefined;
  }
  return requested;
}

// The date the receipt (read-only order view) shows in its date field: the
// customer's own pick when the flag is on and the order carries one,
// otherwise exactly today's value (NAV's expected date).
export function receiptDeliveryDate(
  order: DeliveryDates,
  enabled: boolean,
): Date {
  const requested = enabled ? order.requestedDelivery : undefined;
  return validDate(requested) ? requested : order.latestDelivery;
}

// The receipt's small "Expected {date}" note under the customer's own pick -
// only when the two differ and the expected date is real.
export function expectedDeliveryHint(
  order: DeliveryDates,
  enabled: boolean,
): string | undefined {
  if (!requestedDeliveryToShow(order, enabled)) return undefined;
  const text = validDate(order.latestDelivery)
    ? formatDate(order.latestDelivery)
    : "";
  return text || undefined;
}
